import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Client, ReconnectionTimeMode, TickerStrategy} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

interface WallTeamDTO {
    name: string;
    tag: string | null;
    logo: string | null;
    liters: number;
}

interface WallRungDTO {
    liters: number;
    label: string | null;
    splashEnabled: boolean;
    splashVariant: 'strip' | 'band' | 'slab' | 'takeover' | 'legend' | null;
    splashDurationMs: number;
    splashHeadline: string | null;
    splashSubline: string | null;
}

interface WallDashboardDTO {
    teams: WallTeamDTO[];
    rungs: WallRungDTO[];
    editionName: string | null;
    seriesLabel: string | null;
    matchesPlayed: number;
    sponsorLogoUrl: string | null;
    sponsorLogoAlt: string | null;
    sponsorEyebrow: string | null;
    sponsorCaption: string | null;
    sponsorEnabled: boolean;
    accentColor: string | null;
    panelRotateMs: number;
    paused: boolean;
    adQueueMax: number;
    adQueueTtlMinutes: number;
}

interface AdSpot {
    uuid: string;
    source: 'VIMEO' | 'YOUTUBE';
    title: string;
    embedId: string;
    soundEnabled: boolean;
    volume: number;
    trimStartSeconds: number;
    trimEndSeconds: number | null;
    durationSeconds: number | null;
    startedAtEpochMs: number;
    preview: boolean;
}

interface AdNowPlayingMessage {
    playing: AdSpot | null;
}

interface SplashJob {
    key: string;
    variant: 'strip' | 'band' | 'slab' | 'takeover' | 'legend';
    headline: string;
    subline: string;
    liters: number;
    teamName: string;
    durationMs: number;
}

const WALL_TOPIC = '/topic/wall-dashboard';
const WALL_PREVIEW_TOPIC = '/topic/wall-dashboard-preview';
const WALL_AD_TOPIC = '/topic/wall-ad';

const DEFAULT_ACCENT = '#6c5dd3';
const DEFAULT_ROTATE_MS = 9000;
// Shared with BeerIndexWall (landscape) on purpose: a rung crossing is a real-world event
// independent of which visual variant is on screen, so both must agree on what already fired.
const FIRED_STORAGE_KEY = 'beerIndexWall.firedRungs';

// Poppins, not Archivo/IBM Plex Mono - the project's own theme font (already loaded globally
// via src/styles/settings/_index.scss's $font-family-base), so the wall matches the rest of the app.
const MONO = "'Poppins',sans-serif";
const DISPLAY = "'Poppins',sans-serif";

const STAGE_W = 1080;
const STAGE_H = 1920;

const loadFiredKeys = (): Set<string> => {
    try {
        return new Set(JSON.parse(sessionStorage.getItem(FIRED_STORAGE_KEY) || '[]'));
    } catch {
        return new Set();
    }
};

const saveFiredKeys = (keys: Set<string>) => {
    try {
        sessionStorage.setItem(FIRED_STORAGE_KEY, JSON.stringify(Array.from(keys)));
    } catch {
        // ignore
    }
};

const TeamCrest = ({team, size, fontSize}: { team: WallTeamDTO, size: number, fontSize: number }) => (
    <div style={{
        width: size, height: size, flexShrink: 0, borderRadius: '50%', background: '#f5f3ee',
        border: '1px solid rgba(255,255,255,.2)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', overflow: 'hidden',
    }}>
        {team.logo
            ? <img src={team.logo} alt={team.name} style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
            : <span style={{fontWeight: 900, fontStretch: '75%' as any, fontSize, color: '#0a0e13'}}>
                {(team.tag || team.name || '').slice(0, 2).toUpperCase()}
              </span>}
    </div>
);

// rotated (90/270) swaps which viewport axis the fixed 1080x1920 stage has to fit inside.
const usePortraitStageScale = (rotated: boolean) => {
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const onResize = () => setScale(rotated
            ? Math.min(window.innerWidth / STAGE_H, window.innerHeight / STAGE_W)
            : Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H));
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [rotated]);
    return scale;
};

// Smart-TV kiosk browsers (Hisense VIDAA and other common TV OSes) self-identify in their UA
// string - a tablet, phone or desktop browser never matches any of these tokens.
const isSmartTvBrowser = (): boolean =>
    /VIDAA|Hisense|Tizen|Web0S|webOS|GoogleTV|AFTT|AFTB|AFTS|CrKey|SMART-TV|HbbTV/i.test(navigator.userAgent || '');

const ROTATE_STEPS_STORAGE_KEY = 'bv-rotate-steps';

const loadRotateSteps = (): number => {
    try {
        return parseInt(localStorage.getItem(ROTATE_STEPS_STORAGE_KEY) || '0', 10) || 0;
    } catch {
        return 0;
    }
};

const BeerIndexWallVertical = () => {
    const [rotateSteps, setRotateSteps] = useState<number>(loadRotateSteps);
    const rotated = rotateSteps === 1 || rotateSteps === 3;
    const scale = usePortraitStageScale(rotated);
    const toggleRotate = () => {
        setRotateSteps(prev => {
            const next = (prev + 1) % 4;
            try {
                localStorage.setItem(ROTATE_STEPS_STORAGE_KEY, String(next));
            } catch {
                // ignore
            }
            return next;
        });
    };
    const [data, setData] = useState<WallDashboardDTO | null>(null);
    const [clock, setClock] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [splashQueue, setSplashQueue] = useState<SplashJob[]>([]);
    const prevTeamsRef = useRef<Map<string, number>>(new Map());
    const prevRankRef = useRef<Map<string, number>>(new Map());
    const hotStreakBaseRef = useRef<Map<string, number>>(new Map());
    const firedKeysRef = useRef<Set<string>>(loadFiredKeys());
    const [rankDeltas, setRankDeltas] = useState<Map<string, number>>(new Map());
    const [hotStreak, setHotStreak] = useState<{ team: WallTeamDTO, gain: number }[]>([]);
    const isFirstLoad = useRef(true);
    const [adPlaying, setAdPlaying] = useState<AdSpot | null>(null);
    const adPlayingRef = useRef<AdSpot | null>(null);
    // Splashes triggered while a commercial is playing wait here instead of showing immediately -
    // flushed into splashQueue once the ad stops (see the wall-ad subscription below).
    const pendingSplashesRef = useRef<{ job: SplashJob, queuedAtMs: number }[]>([]);
    const adQueueTtlMinutesRef = useRef<number>(2);

    useEffect(() => {
        adPlayingRef.current = adPlaying;
    }, [adPlaying]);

    useEffect(() => {
        document.title = 'BarVictory - Indexul Berii';
    }, []);

    // Same reasoning as BeerIndexWall: the wall is nested inside the app's normal layout, so page
    // scroll must be locked or the hidden chrome behind this fixed overlay can trigger a native
    // scrollbar whose gutter clips the stage.
    useEffect(() => {
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
        };
    }, []);

    useEffect(() => {
        const tick = () => setClock(new Date().toLocaleTimeString('ro-RO', {hour12: false}));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const applyDashboard = (dashboard: WallDashboardDTO) => {
            const nextRanks = new Map<string, number>();
            [...dashboard.teams]
                .sort((a, b) => b.liters - a.liters)
                .forEach((t, i) => nextRanks.set(t.name, i + 1));

            if (!isFirstLoad.current) {
                const deltas = new Map<string, number>();
                nextRanks.forEach((rank, name) => {
                    const prevRank = prevRankRef.current.get(name);
                    deltas.set(name, prevRank != null ? prevRank - rank : 0);
                });
                setRankDeltas(deltas);

                const gains = dashboard.teams
                    .map(t => ({team: t, gain: t.liters - (hotStreakBaseRef.current.get(t.name) ?? t.liters)}))
                    .filter(g => g.gain > 0)
                    .sort((a, b) => b.gain - a.gain)
                    .slice(0, 4);
                setHotStreak(gains);

                if (!dashboard.paused) {
                    detectSplashes(dashboard);
                }
            } else {
                // Seed: mark every rung already below a team's current total as fired, so a
                // fresh wall never splash-storms on boot.
                const seeded = new Set(firedKeysRef.current);
                dashboard.teams.forEach(team => {
                    dashboard.rungs.forEach(rung => {
                        if (team.liters >= rung.liters) {
                            seeded.add(`${team.name}:${rung.liters}`);
                        }
                    });
                });
                firedKeysRef.current = seeded;
                saveFiredKeys(seeded);
                isFirstLoad.current = false;
            }

            prevRankRef.current = nextRanks;
            const litersSnapshot = new Map<string, number>();
            dashboard.teams.forEach(t => litersSnapshot.set(t.name, t.liters));
            prevTeamsRef.current = litersSnapshot;
            if (Math.random() < 1 / 24) {
                hotStreakBaseRef.current = litersSnapshot;
            }
        };

        const detectSplashes = (dashboard: WallDashboardDTO) => {
            const newJobs: SplashJob[] = [];
            dashboard.teams.forEach(team => {
                const before = prevTeamsRef.current.get(team.name);
                if (before == null) return;
                dashboard.rungs.forEach(rung => {
                    if (!rung.splashEnabled || !rung.splashVariant) return;
                    const key = `${team.name}:${rung.liters}`;
                    if (before < rung.liters && team.liters >= rung.liters && !firedKeysRef.current.has(key)) {
                        firedKeysRef.current.add(key);
                        newJobs.push({
                            key,
                            variant: rung.splashVariant,
                            headline: rung.splashHeadline || `PRAG // ${rung.liters}L ATINS`,
                            subline: rung.splashSubline || '',
                            liters: rung.liters,
                            teamName: team.name,
                            durationMs: rung.splashDurationMs || 3000,
                        });
                    }
                });
            });
            if (newJobs.length > 0) {
                saveFiredKeys(firedKeysRef.current);
                if (adPlayingRef.current) {
                    // A commercial is showing - the splash queues behind it instead of interrupting.
                    const queueMax = Math.max(1, dashboard.adQueueMax || 3);
                    const queued = [...pendingSplashesRef.current, ...newJobs.map(job => ({job, queuedAtMs: Date.now()}))];
                    pendingSplashesRef.current = queued.slice(-queueMax);
                } else {
                    setSplashQueue(prev => [...prev, ...newJobs]);
                }
            }
        };

        // Public wall - no login, so no Authorization header (see BeerIndexWall for the full
        // reasoning: anonymous STOMP sessions are accepted on the public "/topic" broker prefix).
        const endpoint = process.env.REACT_APP_ENDPOINT ?? '';
        let cancelled = false;

        // First paint doesn't wait on the WebSocket handshake - a plain GET renders the wall
        // immediately, which matters on a flaky mobile/tablet connection where the socket can take
        // a few seconds (or the full connectionTimeout below) to come up. Once this resolves (or
        // fails), the socket connected below is the only source of updates from then on.
        fetch(`${endpoint}/api/wall/dashboard`)
            .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
            .then((dashboard: WallDashboardDTO) => {
                if (cancelled) return;
                adQueueTtlMinutesRef.current = Math.max(1, dashboard.adQueueTtlMinutes || 2);
                applyDashboard(dashboard);
                setData(dashboard);
            })
            .catch(() => {
                // Ignore - the WebSocket's onSubscribe snapshot below still delivers the first paint.
            });

        const isTv = isSmartTvBrowser();
        const client = new Client({
            webSocketFactory: () => new SockJS(`${endpoint}/ws`) as WebSocket,
            // These walls run unattended for hours/days on embedded smart-TV browsers (e.g. Hisense
            // VIDAA), which are slower and flakier than a desktop browser: the main JS thread can
            // stall for seconds under heavy rendering/GC, and Wi-Fi drops are more frequent. Defaults
            // tuned for a normal browser tab read as "delay" or a stuck screen on that hardware.
            // Safe for every device, so applied unconditionally:
            // - connectionTimeout: stompjs's own CONNECT handshake defaults to no timeout (0), so a
            //   SockJS transport that silently never opens (seen on some embedded browsers) leaves the
            //   wall stuck with no reconnect attempt at all. Bound it so a hung handshake gets aborted
            //   and retried instead.
            connectionTimeout: 15000,
            // - heartbeatStrategy 'worker': runs the heartbeat ticker off the main thread. On a
            //   normal 'interval' ticker, a busy main thread (splash/ad rendering) can delay the
            //   outgoing ping or miss the incoming one past heartbeatIncoming, causing stompjs to
            //   treat a perfectly fine connection as dead and reconnect unnecessarily. Falls back to
            //   'interval' automatically if the browser has no Worker support.
            heartbeatStrategy: TickerStrategy.Worker,
            reconnectDelay: 2000,
            // Exponential backoff (2s up to 30s) is only safe for a kiosk TV that connects once and
            // stays up for days: stompjs never resets the backoff after a successful reconnect (only
            // a fresh activate() does), so it climbs monotonically for the life of the page. A TV
            // barely ever reconnects, so it never notices. An interactive tablet/phone/desktop viewer
            // reconnects far more often (screen lock, backgrounding, sleep, walking out of Wi-Fi
            // range) and would otherwise ratchet up to a 30s-stuck wall after a handful of drops -
            // worse than the flat 5s-forever retry this replaced. Non-TV viewers get the plain flat
            // reconnectDelay instead (stompjs's default LINEAR mode).
            ...(isTv ? {reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL, maxReconnectDelay: 30000} : {}),
            onConnect: () => {
                client.subscribe(WALL_TOPIC, (frame) => {
                    try {
                        const dashboard: WallDashboardDTO = JSON.parse(frame.body);
                        adQueueTtlMinutesRef.current = Math.max(1, dashboard.adQueueTtlMinutes || 2);
                        applyDashboard(dashboard);
                        setData(dashboard);
                    } catch {
                        // Ignore malformed frames - keep the last good data on screen.
                    }
                });
                // Commercial rotation - same "now playing" state for every connected wall. A splash
                // that fired while a spot was playing was queued in pendingSplashesRef; the moment
                // playing goes back to null, flush whatever's still fresh into the real splash queue.
                client.subscribe(WALL_AD_TOPIC, (frame) => {
                    try {
                        const message: AdNowPlayingMessage = JSON.parse(frame.body);
                        const wasPlaying = adPlayingRef.current;
                        setAdPlaying(message.playing);
                        adPlayingRef.current = message.playing;

                        if (wasPlaying && !message.playing && pendingSplashesRef.current.length > 0) {
                            const ttlMs = adQueueTtlMinutesRef.current * 60000;
                            const now = Date.now();
                            const fresh = pendingSplashesRef.current.filter(p => now - p.queuedAtMs <= ttlMs);
                            pendingSplashesRef.current = [];
                            if (fresh.length > 0) {
                                setSplashQueue(prev => [...prev, ...fresh.map(p => p.job)]);
                            }
                        }
                    } catch {
                        // Ignore malformed ad frames - keep whatever was last shown.
                    }
                });
                client.subscribe(WALL_PREVIEW_TOPIC, (frame) => {
                    try {
                        const rung: WallRungDTO = JSON.parse(frame.body);
                        if (!rung.splashVariant) return;
                        setSplashQueue(prev => [...prev, {
                            key: `preview:${Date.now()}`,
                            variant: rung.splashVariant as SplashJob['variant'],
                            headline: rung.splashHeadline || `PRAG // ${rung.liters}L ATINS`,
                            subline: rung.splashSubline || '',
                            liters: rung.liters,
                            teamName: 'PREVIZUALIZARE',
                            durationMs: rung.splashDurationMs || 3000,
                        }]);
                    } catch {
                        // Ignore malformed preview frames.
                    }
                });
            },
            onStompError: () => {
                // Connection errors are silently swallowed; reconnect happens automatically.
            },
        });

        client.activate();
        return () => {
            cancelled = true;
            client.deactivate();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const rotateMs = Math.max(3000, data?.panelRotateMs || DEFAULT_ROTATE_MS);
        if (data?.paused) return;
        const id = setInterval(() => setActiveTab(prev => (prev + 1) % 3), rotateMs);
        return () => clearInterval(id);
    }, [data?.panelRotateMs, data?.paused]);

    useEffect(() => {
        if (splashQueue.length === 0) return;
        const current = splashQueue[0];
        const timer = setTimeout(() => setSplashQueue(prev => prev.slice(1)), current.durationMs);
        return () => clearTimeout(timer);
    }, [splashQueue]);

    const accent = data?.accentColor || DEFAULT_ACCENT;

    const standings = useMemo(() => {
        if (!data) return [];
        return [...data.teams]
            .sort((a, b) => b.liters - a.liters)
            .map((t, i) => ({team: t, rank: i + 1, delta: rankDeltas.get(t.name) ?? 0}));
    }, [data, rankDeltas]);

    const leader = standings[0]?.team;
    const second = standings[1]?.team;
    const gap = leader && second ? Math.max(0, leader.liters - second.liters) : 0;

    const topCards = useMemo(() => standings.slice(0, 4).map(s => ({
        ...s,
        avg: data?.matchesPlayed ? (s.team.liters / data.matchesPlayed).toFixed(1) : s.team.liters.toFixed(1),
    })), [standings, data?.matchesPlayed]);

    const statTiles = useMemo(() => {
        if (!data) return [];
        const sum = data.teams.reduce((acc, t) => acc + t.liters, 0);
        const avgPerMatch = data.matchesPlayed ? sum / data.teams.length / data.matchesPlayed : 0;
        const over75 = data.teams.filter(t => t.liters >= 75).length;
        return [
            {val: Math.round(sum).toLocaleString(), label: 'TOTAL TURNAT · L', color: accent},
            {val: avgPerMatch.toFixed(1), label: 'MEDIA EDITIEI · L / MECI', color: '#fff'},
            {val: String(over75), label: 'ECHIPE PESTE 75 L', color: '#fff'},
            {val: gap.toFixed(1), label: 'AVANS LIDER · L', color: '#fff'},
        ];
    }, [data, accent, gap]);

    const movers = useMemo(() => hotStreak.slice(0, 3).map(h => ({...h})), [hotStreak]);

    const ladder = useMemo(() => {
        if (!data) return [];
        return data.rungs.map(rung => {
            const below = [...data.teams].filter(t => t.liters < rung.liters).sort((a, b) => b.liters - a.liters);
            if (below.length === 0) {
                return {rung, chaser: null as WallTeamDTO | null, pct: 100, stat: `✓ ${data.teams.length}/${data.teams.length}`, complete: true};
            }
            const chaser = below[0];
            return {
                rung, chaser,
                pct: Math.min(100, (chaser.liters / rung.liters) * 100),
                stat: `${chaser.liters.toFixed(1)} / ${rung.liters}`,
                complete: false,
            };
        });
    }, [data]);

    if (!data) {
        return <div style={{position: 'fixed', inset: 0, background: '#05070a'}}/>;
    }

    // Centering on the stage's own midpoint (rather than its top-left corner) keeps it centered
    // in place across all 4 rotate steps - translate is computed once, rotate just spins in place.
    const ox = window.innerWidth / 2 - STAGE_W / 2;
    const oy = window.innerHeight / 2 - STAGE_H / 2;

    return (
        <div style={{position: 'fixed', inset: 0, background: '#05070a', overflow: 'hidden'}}>
            <style>{`
                @keyframes msPop{0%{opacity:0;transform:scale(.96)}8%{opacity:1;transform:scale(1)}88%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(1.02)}}
                @keyframes blinkTick{0%,100%{opacity:1}50%{opacity:.25}}
                .wall-scroll-hidden{scrollbar-width:none;-ms-overflow-style:none}
                .wall-scroll-hidden::-webkit-scrollbar{display:none}
            `}</style>
            <div style={{
                position: 'absolute', left: 0, top: 0, width: STAGE_W, height: STAGE_H,
                transformOrigin: `${STAGE_W / 2}px ${STAGE_H / 2}px`,
                transform: `translate(${ox}px, ${oy}px) rotate(${rotateSteps * 90}deg) scale(${scale})`,
                display: 'flex', flexDirection: 'column',
                gap: 20, padding: 28, boxSizing: 'border-box', fontFamily: DISPLAY, color: '#fff', background: '#080b0f',
            }}>
                <Header accent={accent} clock={clock} seriesLabel={data.seriesLabel} onRotateClick={toggleRotate}/>
                <Hero accent={accent} data={data} leader={leader} second={second} gap={gap}/>
                <Tabs accent={accent} activeTab={activeTab}/>
                <RotatingPanel activeTab={activeTab} accent={accent} topCards={topCards} statTiles={statTiles} movers={movers} ladder={ladder}/>
                <StandingsLadderGrid accent={accent} standings={standings} ladder={ladder}/>
                {movers.length > 0 && <HotStreakCard accent={accent} hotStreak={movers}/>}
                {data.sponsorEnabled && data.sponsorLogoUrl && <SponsorStrip data={data}/>}
                {/* Rendered inside the scaled stage (unlike a plain unscaled overlay) so its
                    absolute pixel values - up to 960px wide - stay proportional to the visible
                    stage instead of overflowing it once the 1080x1920 canvas is scaled down. */}
                {splashQueue[0] && <SplashOverlay job={splashQueue[0]} accent={accent}/>}
            </div>
            {/* Unlike splash cards (fixed design pixel sizes that need the stage's scale to look
                right), a video has no intrinsic size to preserve - it should always cover the whole
                physical screen during a commercial break, not just the letterboxed TV canvas. So
                this sits outside the scaled stage, sized to the real fixed viewport wrapper above -
                but it still has to honor the same screen-rotation correction, or a commercial would
                snap back to the wrong orientation every time one plays. */}
            {adPlaying && <AdOverlay ad={adPlaying} rotateSteps={rotateSteps}/>}
        </div>
    );
};

const Header = ({accent, clock, seriesLabel, onRotateClick}: {
    accent: string, clock: string, seriesLabel: string | null, onRotateClick: () => void
}) => (
    <div style={{
        height: 82, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,.08)', paddingBottom: 4, marginBottom: -8,
    }}>
        <div style={{display: 'flex', alignItems: 'center', gap: 22}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                <div style={{
                    width: 44, height: 44, borderRadius: 11, background: accent, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 29,
                    color: '#0a0e13', lineHeight: 1,
                }}>V</div>
                <div style={{fontWeight: 900, fontSize: 32, letterSpacing: '.01em'}}>
                    BAR<span style={{color: accent}}>VICTORY</span>
                </div>
            </div>
            <div style={{width: 1, height: 46, background: 'rgba(255,255,255,.1)'}}/>
            <div style={{lineHeight: 1.15}}>
                <div style={{fontWeight: 900, fontSize: 30}}>Indexul Berii</div>
                <div style={{fontFamily: MONO, fontSize: 13, letterSpacing: '.16em', color: 'rgba(255,255,255,.42)', marginTop: 3}}>
                    {(seriesLabel || '').toUpperCase()}
                </div>
            </div>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 9, height: 38, padding: '0 16px', borderRadius: 9,
                    background: 'rgba(34,197,94,.14)', border: '1px solid rgba(34,197,94,.3)',
                }}>
                    <div style={{width: 9, height: 9, borderRadius: '50%', background: '#22c55e', animation: 'blinkTick 1.4s infinite'}}/>
                    <span style={{fontFamily: MONO, fontSize: 14, fontWeight: 600, letterSpacing: '.16em', color: '#4ade80'}}>LIVE</span>
                </div>
                {/* Corrects for a physical screen mounted rotated relative to how this portrait
                    layout is authored - cycles 0/90/180/270 and persists the choice (see
                    'bv-rotate-steps' below) so a reload doesn't lose the correction. */}
                <button onClick={onRotateClick} title={'Rotire ecran 90°'} aria-label={'Rotire ecran 90 grade'} style={{
                    width: 38, height: 38, borderRadius: 9, background: 'rgba(255,255,255,.06)',
                    border: '1px solid rgba(255,255,255,.16)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', padding: 0,
                }}>
                    <svg width={'18'} height={'18'} viewBox={'0 0 24 24'} fill={'none'} stroke={'#fff'} strokeWidth={2} strokeLinecap={'round'} strokeLinejoin={'round'}>
                        <path d={'M3 12a9 9 0 1 0 3-6.7'}/>
                        <path d={'M3 4v5h5'}/>
                    </svg>
                </button>
            </div>
            <span style={{fontFamily: MONO, fontSize: 13, letterSpacing: '.2em', color: 'rgba(255,255,255,.42)'}}>{clock}</span>
        </div>
    </div>
);

const Hero = ({accent, data, leader, second, gap}: {
    accent: string, data: WallDashboardDTO, leader?: WallTeamDTO, second?: WallTeamDTO, gap: number
}) => {
    if (!leader) return null;
    return (
        <div style={{position: 'relative', flex: 'none', height: 306, borderRadius: 22, overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)'}}>
            <div style={{
                position: 'absolute', inset: 0,
                background: `radial-gradient(120% 90% at 50% 18%, rgba(249,168,60,.35), rgba(120,53,15,.28) 42%, rgba(10,14,19,.96) 78%), linear-gradient(180deg,#12100c,#0a0e13)`,
            }}/>
            <div style={{position: 'absolute', inset: 0, background: `radial-gradient(60% 50% at 50% 46%, ${accent}38, transparent 70%)`}}/>
            <div style={{position: 'absolute', top: 24, left: 0, right: 0, textAlign: 'center', fontFamily: MONO, fontSize: 14, fontWeight: 500, letterSpacing: '.32em', color: 'rgba(255,255,255,.6)'}}>
                INDEXUL BERII — LIDER IN DIRECT
            </div>
            <div style={{position: 'absolute', top: 66, left: 40, display: 'flex', alignItems: 'center', gap: 16}}>
                <TeamCrest team={leader} size={68} fontSize={27}/>
                <div style={{lineHeight: 1.05}}>
                    <div style={{fontFamily: MONO, fontSize: 12, letterSpacing: '.2em', color: 'rgba(255,255,255,.45)', marginBottom: 5}}>LOCUL 01</div>
                    <div style={{fontWeight: 900, fontSize: 27, textTransform: 'uppercase', maxWidth: 300}}>{leader.name}</div>
                </div>
            </div>
            {second && (
                <div style={{position: 'absolute', top: 66, right: 40, display: 'flex', alignItems: 'center', gap: 16, flexDirection: 'row-reverse', textAlign: 'right'}}>
                    <TeamCrest team={second} size={68} fontSize={27}/>
                    <div style={{lineHeight: 1.05}}>
                        <div style={{fontFamily: MONO, fontSize: 12, letterSpacing: '.2em', color: 'rgba(255,255,255,.45)', marginBottom: 5}}>LOCUL 02</div>
                        <div style={{fontWeight: 900, fontSize: 27, textTransform: 'uppercase', maxWidth: 300}}>{second.name}</div>
                    </div>
                </div>
            )}
            <div style={{position: 'absolute', top: 150, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 34}}>
                <div style={{fontWeight: 900, fontSize: 84, lineHeight: 1, color: '#fff'}}>{leader.liters.toFixed(1)}</div>
                <div style={{fontWeight: 900, fontSize: 58, lineHeight: 1, color: accent}}>VS</div>
                <div style={{fontWeight: 900, fontSize: 84, lineHeight: 1, color: 'rgba(255,255,255,.55)'}}>{(second?.liters ?? 0).toFixed(1)}</div>
            </div>
            <div style={{position: 'absolute', top: 234, left: 0, right: 0, textAlign: 'center', fontFamily: MONO, fontSize: 14, letterSpacing: '.24em', color: 'rgba(255,255,255,.5)'}}>
                LITRI TURNATI · DIFERENTA {gap.toFixed(1)} L
            </div>
            <div style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, height: 48, background: 'rgba(5,7,10,.6)',
                borderTop: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <span style={{fontFamily: MONO, fontSize: 13, letterSpacing: '.18em', color: 'rgba(255,255,255,.6)'}}>
                    {(data.editionName || 'BARVICTORY').toUpperCase()}
                </span>
            </div>
        </div>
    );
};

const TabDef = [
    {label: 'Clasament', icon: <path d="M8 21h8M12 17v4M6 4h12v5a6 6 0 0 1-12 0z"/>},
    {label: 'Statistici', icon: <path d="M18 20V10M12 20V4M6 20v-6"/>},
    {label: 'Praguri', icon: <path d="M4 22V4a1 1 0 0 1 1-1h13l-3 5 3 5H6"/>},
];

const Tabs = ({accent, activeTab}: { accent: string, activeTab: number }) => (
    <div style={{flex: 'none', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12}}>
        {TabDef.map((tab, i) => {
            const isActive = activeTab === i;
            return (
                <div key={tab.label} style={{
                    height: 60, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 9, transition: 'all .4s',
                    background: isActive ? `${accent}1f` : 'rgba(255,255,255,.04)',
                    border: `1px solid ${isActive ? `${accent}66` : 'rgba(255,255,255,.07)'}`,
                }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={isActive ? accent : 'rgba(255,255,255,.6)'} strokeWidth={1.9}>
                        {tab.icon}
                    </svg>
                    <span style={{fontWeight: isActive ? 700 : 600, fontSize: 16, color: isActive ? accent : 'rgba(255,255,255,.6)'}}>{tab.label}</span>
                </div>
            );
        })}
    </div>
);

const RotatingPanel = ({activeTab, accent, topCards, statTiles, movers, ladder}: any) => (
    <div style={{flex: 'none', minHeight: 424}}>
        {activeTab === 0 && (
            <div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14}}>
                    <div style={{fontWeight: 900, fontSize: 24}}>Litri pe Echipa</div>
                    <div style={{
                        display: 'flex', alignItems: 'center', height: 32, padding: '0 14px', borderRadius: 8,
                        background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)',
                    }}>
                        <span style={{fontFamily: MONO, fontSize: 12, letterSpacing: '.14em', color: 'rgba(255,255,255,.55)'}}>MEDIA EDITIEI</span>
                    </div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                    {topCards.map((c: any, i: number) => (
                        <div key={c.team.name} style={{
                            borderRadius: 14, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16, overflow: 'hidden',
                            background: i === 0 ? `${accent}14` : 'rgba(255,255,255,.04)',
                            border: `1px solid ${i === 0 ? `${accent}55` : 'rgba(255,255,255,.07)'}`,
                        }}>
                            <TeamCrest team={c.team} size={54} fontSize={21}/>
                            <div style={{flex: 1, minWidth: 0}}>
                                <div style={{fontWeight: 800, fontSize: 18, textTransform: 'uppercase', letterSpacing: '.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                    {c.team.name}
                                </div>
                            </div>
                            <div style={{textAlign: 'right', flex: 'none'}}>
                                <div style={{fontWeight: 900, fontSize: 40, lineHeight: 1, color: i === 0 ? accent : '#fff'}}>{c.avg}</div>
                                <div style={{fontFamily: MONO, fontSize: 10, letterSpacing: '.18em', color: 'rgba(255,255,255,.4)', marginTop: 3}}>L / MECI</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {activeTab === 1 && (
            <div>
                <div style={{fontWeight: 900, fontSize: 24, marginBottom: 14}}>Statistici Editie</div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 18}}>
                    {statTiles.map((s: any) => (
                        <div key={s.label} style={{borderRadius: 14, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', padding: 18}}>
                            <div style={{fontWeight: 900, fontSize: 34, lineHeight: 1, color: s.color}}>{s.val}</div>
                            <div style={{fontFamily: MONO, fontSize: 10, letterSpacing: '.13em', color: 'rgba(255,255,255,.45)', marginTop: 9}}>{s.label}</div>
                        </div>
                    ))}
                </div>
                {movers.length > 0 && (
                    <>
                        <div style={{fontFamily: MONO, fontSize: 12, letterSpacing: '.22em', color: 'rgba(255,255,255,.4)', marginBottom: 11}}>
                            CELE MAI MARI URCARI RECENTE
                        </div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                            {movers.map((m: any) => (
                                <div key={m.team.name} style={{
                                    display: 'flex', alignItems: 'center', gap: 14, borderRadius: 12,
                                    background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', padding: '12px 18px',
                                }}>
                                    <div style={{fontFamily: MONO, fontSize: 15, color: '#4ade80', width: 38, flex: 'none'}}>▲</div>
                                    <TeamCrest team={m.team} size={32} fontSize={12}/>
                                    <div style={{flex: 1, minWidth: 0}}>
                                        <div style={{fontWeight: 800, fontSize: 18, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                            {m.team.name}
                                        </div>
                                    </div>
                                    <div style={{fontWeight: 900, fontSize: 26}}>
                                        +{m.gain.toFixed(1)}<span style={{fontFamily: MONO, fontSize: 13, color: 'rgba(255,255,255,.4)', marginLeft: 6}}>L</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        )}

        {activeTab === 2 && (
            <div>
                <div style={{fontWeight: 900, fontSize: 24, marginBottom: 14}}>Praguri Atinse</div>
                <div style={{display: 'flex', flexDirection: 'column', gap: 9}}>
                    {ladder.filter((l: any) => l.complete || l.chaser).map((l: any, idx: number) => (
                        <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: 15, borderRadius: 12,
                            background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', padding: '11px 18px',
                        }}>
                            <div style={{
                                minWidth: 58, height: 32, flex: 'none', borderRadius: 8, background: `${accent}2e`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO,
                                fontWeight: 600, fontSize: 14, color: accent,
                            }}>{l.rung.liters}L</div>
                            <div style={{flex: 1, minWidth: 0}}>
                                <div style={{fontWeight: 800, fontSize: 18, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                    {l.complete ? 'Toate echipele au trecut' : l.chaser?.name}
                                </div>
                                <div style={{fontFamily: MONO, fontSize: 11, letterSpacing: '.1em', color: 'rgba(255,255,255,.4)', marginTop: 3}}>
                                    {l.complete ? l.stat : `SE APROPIE DE PRAGUL DE ${l.rung.liters}L`}
                                </div>
                            </div>
                            {!l.complete && (
                                <div style={{fontWeight: 900, fontSize: 24}}>
                                    {l.chaser?.liters.toFixed(1)}<span style={{fontFamily: MONO, fontSize: 12, color: 'rgba(255,255,255,.4)', marginLeft: 6}}>L</span>
                                </div>
                            )}
                        </div>
                    ))}
                    {ladder.length === 0 && (
                        <div style={{fontFamily: MONO, fontSize: 13, color: 'rgba(255,255,255,.4)'}}>
                            Nicio etapa configurata inca.
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
);

const StandingsLadderGrid = ({accent, standings, ladder}: any) => (
    <div style={{flex: 'none', display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 20}}>
        <div className={'wall-scroll-hidden'} style={{borderRadius: 16, background: '#0a0d11', border: '1px solid rgba(255,255,255,.07)', padding: '18px 20px', boxSizing: 'border-box'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11}}>
                <div style={{fontWeight: 900, fontSize: 21}}>Clasament in Direct</div>
                <div style={{fontFamily: MONO, fontSize: 11, letterSpacing: '.14em', color: 'rgba(255,255,255,.4)'}}>
                    TOP {standings.length}
                </div>
            </div>
            <div style={{
                display: 'grid', gridTemplateColumns: '30px 30px 1fr 24px 70px', alignItems: 'center', gap: 10,
                padding: '0 6px 8px', borderBottom: '1px solid rgba(255,255,255,.1)',
            }}>
                <div style={{fontFamily: MONO, fontSize: 10, letterSpacing: '.16em', color: 'rgba(255,255,255,.4)'}}>#</div>
                <div/>
                <div style={{fontFamily: MONO, fontSize: 10, letterSpacing: '.16em', color: 'rgba(255,255,255,.4)'}}>ECHIPA</div>
                <div/>
                <div style={{fontFamily: MONO, fontSize: 10, letterSpacing: '.16em', color: 'rgba(255,255,255,.4)', textAlign: 'right'}}>LITRI</div>
            </div>
            <div className={'wall-scroll-hidden'} style={{height: 250, overflowY: 'auto', marginTop: 2}}>
                {standings.map((s: any) => (
                    <div key={s.team.name} style={{
                        height: 50, flex: 'none', display: 'grid', gridTemplateColumns: '30px 30px 1fr 24px 70px',
                        alignItems: 'center', gap: 10, padding: '0 6px', boxSizing: 'border-box', borderRadius: 9,
                        background: s.rank === 1 ? `${accent}24` : 'transparent',
                    }}>
                        <div style={{fontWeight: 900, fontSize: 19, color: s.rank === 1 ? accent : '#fff'}}>{s.rank}</div>
                        <TeamCrest team={s.team} size={26} fontSize={10}/>
                        <div style={{fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            {s.team.name}
                        </div>
                        <div style={{
                            fontFamily: MONO, fontSize: 13, textAlign: 'center',
                            color: s.delta > 0 ? '#4ade80' : s.delta < 0 ? '#f87171' : 'rgba(255,255,255,.3)',
                        }}>
                            {s.delta > 0 ? '▲' : s.delta < 0 ? '▼' : '·'}
                        </div>
                        <div style={{fontWeight: 800, fontSize: 18, textAlign: 'right'}}>{s.team.liters.toFixed(1)}</div>
                    </div>
                ))}
            </div>
        </div>

        <div style={{borderRadius: 16, background: '#0a0d11', border: '1px solid rgba(255,255,255,.07)', padding: '18px 20px', boxSizing: 'border-box'}}>
            <div style={{fontWeight: 900, fontSize: 21, marginBottom: 11}}>Scara Pragurilor</div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 7}}>
                {ladder.map((l: any, idx: number) => (
                    <div key={idx} style={{
                        borderRadius: 11, padding: '9px 14px',
                        background: l.complete ? 'rgba(34,197,94,.07)' : 'rgba(255,255,255,.04)',
                        border: `1px solid ${l.complete ? '#22c55e55' : 'rgba(255,255,255,.08)'}`,
                    }}>
                        <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6}}>
                            <div style={{fontFamily: MONO, fontSize: 11, letterSpacing: '.18em', color: l.complete ? '#22c55e' : 'rgba(255,255,255,.5)'}}>
                                {l.rung.label || `CURSA CATRE ${l.rung.liters} L`}
                            </div>
                            <div style={{fontFamily: MONO, fontSize: 11, color: 'rgba(255,255,255,.5)'}}>{l.stat}</div>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 11}}>
                            <div style={{
                                fontWeight: 800, fontSize: 14, textTransform: 'uppercase', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
                            }}>{l.complete ? 'Toate au trecut' : l.chaser?.name}</div>
                            <div style={{width: 104, height: 8, borderRadius: 5, background: 'rgba(255,255,255,.08)', overflow: 'hidden', flex: 'none'}}>
                                <div style={{height: '100%', width: `${l.pct}%`, background: l.complete ? '#22c55e' : `linear-gradient(90deg,#fbbf24,${accent})`, transition: 'width .6s ease'}}/>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const HotStreakCard = ({accent, hotStreak}: { accent: string, hotStreak: { team: WallTeamDTO, gain: number }[] }) => (
    <div style={{
        flex: 1, minHeight: 0, borderRadius: 16, background: 'rgba(249,115,22,.10)',
        border: '1px solid rgba(249,115,22,.3)', padding: '16px 18px', boxSizing: 'border-box', overflow: 'hidden',
    }}>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill={accent} stroke="none">
                <path d="M12 23c4.4 0 8-3.1 8-7 0-4.5-4-7-5-11-.6 2.4-2 3.6-3.4 4.7C10 11 8 9.5 8 6.5 6 8.4 4 11 4 16c0 3.9 3.6 7 8 7z"/>
            </svg>
            <span style={{fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: '.22em', color: accent}}>SERIE FIERBINTE</span>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
            {hotStreak.map((h, i) => (
                <div key={h.team.name} style={{display: 'flex', alignItems: 'center', gap: 11}}>
                    <TeamCrest team={h.team} size={30} fontSize={11}/>
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{
                            fontWeight: 800, fontSize: 14, textTransform: 'uppercase', whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{h.team.name}</div>
                    </div>
                    <div style={{fontWeight: 900, fontSize: 20, color: i === 0 ? accent : '#fff', flex: 'none'}}>+{h.gain.toFixed(1)}</div>
                </div>
            ))}
        </div>
    </div>
);

const SponsorStrip = ({data}: { data: WallDashboardDTO }) => (
    <div style={{
        flex: 'none', height: 190, borderRadius: 16, background: 'linear-gradient(135deg,#fbfaf6,#e9e6dd)',
        border: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 34, padding: '20px 30px', boxSizing: 'border-box',
    }}>
        <div style={{
            fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: '.28em', color: 'rgba(10,14,19,.5)',
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
        } as any}>
            {(data.sponsorEyebrow || 'SPONSOR OFICIAL').toUpperCase()}
        </div>
        <img src={data.sponsorLogoUrl as string} alt={data.sponsorLogoAlt || 'Sponsor'} style={{maxHeight: 150, maxWidth: 560, objectFit: 'contain'}}/>
        <div style={{fontFamily: MONO, fontSize: 12, letterSpacing: '.16em', color: 'rgba(10,14,19,.55)', maxWidth: 200, lineHeight: 1.5}}>
            {(data.sponsorCaption || '').toUpperCase()}
        </div>
    </div>
);

const AdOverlay = ({ad, rotateSteps}: { ad: AdSpot, rotateSteps: number }) => {
    const src = ad.source === 'VIMEO'
        ? `https://player.vimeo.com/video/${ad.embedId}?autoplay=1&muted=${ad.soundEnabled ? 0 : 1}&background=0&title=0&byline=0&portrait=0&controls=0#t=${ad.trimStartSeconds}s`
        : `https://www.youtube.com/embed/${ad.embedId}?autoplay=1&mute=${ad.soundEnabled ? 0 : 1}&start=${ad.trimStartSeconds}&controls=0&modestbranding=1&rel=0`;

    // On some embedded/kiosk browsers, hardware-accelerated video inside an iframe leaves its last
    // frame stuck on screen after the iframe node is removed from the DOM - a plain unmount doesn't
    // reliably tear down that decode session. Forcing the iframe to about:blank just before removal
    // makes the browser release it immediately instead of visually "freezing" until the next ad.
    const iframeRef = useRef<HTMLIFrameElement>(null);
    useEffect(() => () => {
        if (iframeRef.current) {
            iframeRef.current.src = 'about:blank';
        }
    }, []);

    // Swapping width/height before rotating (rather than rotating the true viewport box as-is) is
    // what keeps a 90/270 rotation gapless: a WxH box rotated 90 degrees occupies a HxW footprint,
    // which only fully covers the real viewport if that's the size it started as.
    const rotated = rotateSteps === 1 || rotateSteps === 3;
    const width = rotated ? window.innerHeight : window.innerWidth;
    const height = rotated ? window.innerWidth : window.innerHeight;

    return (
        <div style={{
            position: 'fixed', left: '50%', top: '50%', width, height,
            transform: `translate(-50%, -50%) rotate(${rotateSteps * 90}deg)`,
            zIndex: 55, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}>
            <iframe
                ref={iframeRef}
                key={ad.uuid}
                src={src}
                title={ad.title}
                allow={'autoplay; fullscreen'}
                style={{width: '100%', height: '100%', border: 'none'}}
            />
            <div style={{
                position: 'absolute', top: 24, left: 24, display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(10,14,19,.7)', borderRadius: 9, padding: '6px 14px',
            }}>
                <span style={{fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: '.16em', color: 'rgba(255,255,255,.75)'}}>
                    {ad.preview ? 'PREVIZUALIZARE RECLAMA' : 'RECLAMA'}
                </span>
            </div>
        </div>
    );
};

const SplashOverlay = ({job, accent}: { job: SplashJob, accent: string }) => {
    const animation = `msPop ${job.durationMs}ms cubic-bezier(.2,.9,.3,1) both`;

    if (job.variant === 'strip') {
        return (
            <div style={{position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 60, display: 'flex', justifyContent: 'center', paddingBottom: 150, animation}}>
                <div style={{display: 'flex', alignItems: 'stretch', background: 'rgba(10,14,19,.94)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 30px 90px rgba(0,0,0,.6)', maxWidth: 960}}>
                    <div style={{width: 118, flex: 'none', background: 'rgba(255,255,255,.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2}}>
                        <div style={{fontWeight: 900, fontSize: 48, lineHeight: 1, color: '#e5e5e5'}}>{job.liters}</div>
                        <div style={{fontFamily: MONO, fontSize: 11, letterSpacing: '.26em', color: 'rgba(255,255,255,.5)'}}>LITRI</div>
                    </div>
                    <div style={{padding: '24px 38px 24px 30px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8, minWidth: 0}}>
                        <div style={{fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: '.34em', color: 'rgba(255,255,255,.5)'}}>{job.headline}</div>
                        <div style={{fontWeight: 900, fontSize: 46, lineHeight: .98, textTransform: 'uppercase', color: '#fff'}}>{job.teamName}</div>
                    </div>
                </div>
            </div>
        );
    }
    if (job.variant === 'band') {
        return (
            <div style={{position: 'absolute', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', background: 'rgba(5,6,8,.72)', animation}}>
                <div style={{width: '100%', background: 'linear-gradient(160deg,#1e3a8a,#2563eb 55%,#60a5fa)', padding: '70px 70px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 26, boxShadow: '0 0 120px rgba(37,99,235,.5)'}}>
                    <div>
                        <div style={{fontFamily: MONO, fontSize: 16, fontWeight: 600, letterSpacing: '.4em', color: 'rgba(255,255,255,.82)'}}>{job.headline}</div>
                        <div style={{fontWeight: 900, fontSize: 96, lineHeight: .96, textTransform: 'uppercase', color: '#fff', marginTop: 12}}>{job.teamName}</div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'baseline', gap: 20}}>
                        <div style={{fontWeight: 900, fontSize: 150, lineHeight: .9, color: 'rgba(255,255,255,.92)'}}>{job.liters}</div>
                        <div style={{fontFamily: MONO, fontSize: 18, fontWeight: 600, letterSpacing: '.36em', color: 'rgba(255,255,255,.75)'}}>LITRI</div>
                    </div>
                </div>
            </div>
        );
    }
    if (job.variant === 'slab') {
        return (
            <div style={{position: 'absolute', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,6,8,.82)', animation}}>
                <div style={{width: 940, height: 760, background: 'linear-gradient(135deg,#fbbf24,#f59e0b)', color: '#1a1206', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 70px', boxSizing: 'border-box'}}>
                    <div style={{position: 'absolute', right: -40, bottom: -110, fontWeight: 900, fontSize: 460, lineHeight: 1, color: 'rgba(26,18,6,.13)', pointerEvents: 'none'}}>{job.liters}</div>
                    <div style={{fontFamily: MONO, fontSize: 17, fontWeight: 600, letterSpacing: '.42em', position: 'relative'}}>{job.headline}</div>
                    <div style={{fontWeight: 900, fontSize: 104, lineHeight: .94, textTransform: 'uppercase', marginTop: 14, position: 'relative'}}>{job.teamName}</div>
                    <div style={{display: 'flex', alignItems: 'baseline', gap: 16, marginTop: 20, position: 'relative'}}>
                        <div style={{fontWeight: 900, fontSize: 70, lineHeight: 1}}>{job.liters.toFixed(1)}</div>
                        <div style={{fontFamily: MONO, fontSize: 18, fontWeight: 600, letterSpacing: '.3em'}}>LITRI TURNATI</div>
                    </div>
                </div>
            </div>
        );
    }
    if (job.variant === 'takeover') {
        return (
            <div style={{position: 'absolute', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,6,8,.85)', animation}}>
                <div style={{width: 960, background: accent, color: '#0a0e13', padding: '78px 70px', boxSizing: 'border-box', position: 'relative'}}>
                    <div style={{position: 'absolute', top: 22, left: 26, width: 14, height: 1, background: '#0a0e13'}}/>
                    <div style={{position: 'absolute', top: 22, left: 26, width: 1, height: 14, background: '#0a0e13'}}/>
                    <div style={{position: 'absolute', bottom: 22, right: 26, width: 14, height: 1, background: '#0a0e13'}}/>
                    <div style={{position: 'absolute', bottom: 22, right: 26, width: 1, height: 14, background: '#0a0e13'}}/>
                    <div style={{fontFamily: MONO, fontSize: 18, fontWeight: 600, letterSpacing: '.4em'}}>{job.headline}</div>
                    <div style={{fontWeight: 900, fontSize: 110, lineHeight: .95, textTransform: 'uppercase', marginTop: 10}}>{job.teamName}</div>
                    <div style={{display: 'flex', alignItems: 'baseline', gap: 18, marginTop: 14}}>
                        <div style={{fontWeight: 900, fontSize: 84, lineHeight: 1}}>{job.liters.toFixed(1)}</div>
                        <div style={{fontFamily: MONO, fontSize: 20, fontWeight: 600, letterSpacing: '.3em'}}>LITRI</div>
                    </div>
                </div>
            </div>
        );
    }
    // legend
    return (
        <div style={{position: 'absolute', inset: 0, zIndex: 60, background: '#05070a', animation}}>
            <div style={{position: 'absolute', inset: 0, background: `radial-gradient(70% 50% at 50% 46%, ${accent}66, rgba(120,53,15,.3) 45%, rgba(5,7,10,1) 82%)`}}/>
            <div style={{position: 'absolute', inset: 34, border: `2px solid ${accent}8c`}}/>
            <div style={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: '0 60px', boxSizing: 'border-box'}}>
                <div style={{fontFamily: MONO, fontSize: 19, fontWeight: 600, letterSpacing: '.5em', color: accent}}>{job.headline}</div>
                <div style={{
                    fontWeight: 900, fontSize: 280, lineHeight: .86,
                    background: `linear-gradient(180deg,#fde68a,${accent} 60%,#b45309)`,
                    WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                } as any}>{job.liters}</div>
                <div style={{fontWeight: 900, fontSize: 88, lineHeight: .98, textTransform: 'uppercase', color: '#fff'}}>{job.teamName}</div>
                {job.subline && (
                    <div style={{fontFamily: MONO, fontSize: 17, fontWeight: 600, letterSpacing: '.42em', color: 'rgba(255,255,255,.6)', marginTop: 16}}>
                        {job.subline}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BeerIndexWallVertical;
